<div class="container-fluid mt-4">
  <h2>SignalR Messaging Test</h2>
  <p class="text-muted">Test System Service messaging methods with real-time SignalR notifications</p>

  <!-- System Service Methods Section -->
  <h3 class="mt-4 mb-3">
    <i class="material-icons" style="vertical-align: middle;">storage</i>
    System Service - Application Messaging
  </h3>
  <p class="text-muted">Use these methods for production messaging (Database + automatic SignalR notifications)</p>

  <!-- Listening Card -->
  <div class="card mb-4">
    <div class="card-header bg-info text-white">
      <h5 class="mb-0">
        <i class="material-icons" style="vertical-align: middle; font-size: 18px;">hearing</i>
        Listen for Messages
        <span *ngIf="isListening" class="badge badge-light text-info ml-2">Listening</span>
        <span *ngIf="!isListening" class="badge badge-light text-secondary ml-2">Not Listening</span>
      </h5>
    </div>
    <div class="card-body">
      <div class="form-group">
        <label for="listeningAppId">Application ID to Listen To</label>
        <input
          type="text"
          class="form-control"
          id="listeningAppId"
          [(ngModel)]="applicationId"
          placeholder="Enter application GUID"
          [disabled]="isListening"
        />
      </div>

      <div class="btn-group" role="group">
        <button
          class="btn btn-info"
          (click)="startListening()"
          [disabled]="!isConnected || isListening || !applicationId"
        >
          Start Listening
        </button>
        <button
          class="btn btn-warning"
          (click)="stopListening()"
          [disabled]="!isListening"
        >
          Stop Listening
        </button>
      </div>

      <div *ngIf="isListening && listeningToApplicationId" class="alert alert-info mt-3 mb-0">
        <strong>Listening to:</strong> <code>app_{{ listeningToApplicationId }}</code><br>
        <small>You will receive real-time notifications for new messages and typing indicators</small>
      </div>

      <p class="text-muted mt-2 mb-0">
        <small>Start listening to receive real-time typing notifications and new message alerts for this application</small>
      </p>
    </div>
  </div>

  <!-- Real Message Card (Database + SignalR) -->
  <div class="card mb-4">
    <div class="card-header bg-danger text-white">
      <h5 class="mb-0">
        <i class="material-icons" style="vertical-align: middle; font-size: 18px;">send</i>
        Send Message (systemService.sendApplicationMessage)
      </h5>
    </div>
    <div class="card-body">
      <div class="form-group">
        <label for="realMessageAppId">Application ID</label>
        <input
          type="text"
          class="form-control"
          id="realMessageAppId"
          [(ngModel)]="applicationId"
          placeholder="Enter application GUID"
        />
      </div>

      <div class="form-group">
        <label for="realMessageText">Message Text</label>
        <textarea
          class="form-control"
          id="realMessageText"
          [(ngModel)]="realMessageText"
          placeholder="Enter message text"
          rows="3"
        ></textarea>
      </div>

      <button
        class="btn btn-danger"
        (click)="sendRealMessage()"
        [disabled]="!applicationId || !realMessageText"
      >
        <i class="material-icons" style="vertical-align: middle; font-size: 18px;">send</i>
        Send Message
      </button>

      <p class="text-muted mt-2 mb-0">
        <small>Saves message to database and automatically notifies all listening clients in real-time</small>
      </p>

      <div *ngIf="realMessageSuccess" class="alert alert-success mt-3">
        <strong>Success:</strong> {{ realMessageSuccess }}
      </div>

      <div *ngIf="realMessageError" class="alert alert-danger mt-3">
        <strong>Error:</strong> {{ realMessageError }}
      </div>
    </div>
  </div>

  <!-- Get Messages Card -->
  <div class="card mb-4">
    <div class="card-header bg-primary text-white">
      <h5 class="mb-0">
        <i class="material-icons" style="vertical-align: middle; font-size: 18px;">inbox</i>
        View Messages (systemService.getWorkflowApplicationMessages)
        <button
          class="btn btn-sm btn-light float-right"
          (click)="loadApplicationMessages()"
          [disabled]="!applicationId || loadingMessages"
        >
          <i class="material-icons" style="vertical-align: middle; font-size: 16px;">refresh</i>
          <span *ngIf="loadingMessages">Loading...</span>
          <span *ngIf="!loadingMessages">Refresh</span>
        </button>
      </h5>
    </div>
    <div class="card-body">
      <div class="form-group">
        <label for="messagesAppId">Application ID</label>
        <input
          type="text"
          class="form-control"
          id="messagesAppId"
          [(ngModel)]="applicationId"
          placeholder="Enter application GUID"
          [disabled]="loadingMessages"
        />
      </div>

      <div *ngIf="messagesError" class="alert alert-danger">
        <strong>Error:</strong> {{ messagesError }}
      </div>

      <div *ngIf="applicationMessages.length === 0 && !loadingMessages" class="text-muted text-center py-3">
        No messages loaded. Enter an Application ID and click "Refresh"
      </div>

      <div *ngIf="applicationMessages.length > 0" class="mt-3">
        <h6>Messages ({{ applicationMessages.length }}):</h6>
        <div class="list-group" style="max-height: 400px; overflow-y: auto;">
          <div *ngFor="let msg of applicationMessages"
               class="list-group-item"
               [class.list-group-item-light]="msg.hasRead">
            <div class="d-flex justify-content-between align-items-start">
              <div class="flex-grow-1">
                <h6 class="mb-1">
                  {{ msg.sentBy?.name || 'Unknown' }}
                  <small class="text-muted">{{ msg.sentOn | date:'short' }}</small>
                </h6>
                <p class="mb-1">{{ msg.message }}</p>
                <small class="text-muted">ID: {{ msg.id }}</small>
              </div>
              <div>
                <span *ngIf="msg.hasRead" class="badge badge-success">Read</span>
                <button
                  *ngIf="!msg.hasRead"
                  class="btn btn-sm btn-outline-primary"
                  (click)="markMessageAsRead(msg.id)"
                  title="Mark as read (systemService.markAsRead)"
                >
                  <i class="material-icons" style="vertical-align: middle; font-size: 16px;">done</i>
                  Mark Read
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Event Log Section -->
  <h3 class="mt-5 mb-3">
    <i class="material-icons" style="vertical-align: middle;">list_alt</i>
    Real-time Event Log
  </h3>

  <!-- Messages Log Card -->
  <div class="card mb-4">
    <div class="card-header bg-dark text-white">
      <h5 class="mb-0">
        <i class="material-icons" style="vertical-align: middle; font-size: 18px;">notifications_active</i>
        Live Notifications & Events ({{ messages.length }})
        <button
          class="btn btn-sm btn-outline-light float-right"
          (click)="clearMessages()"
          [disabled]="messages.length === 0"
        >
          Clear
        </button>
      </h5>
    </div>
    <div class="card-body" style="max-height: 400px; overflow-y: auto;">
      <div *ngIf="messages.length === 0" class="text-muted text-center py-3">
        No events yet. Start listening to see real-time typing notifications and new message alerts.
      </div>

      <div *ngFor="let msg of messages" class="border-bottom pb-2 mb-2">
        <div class="d-flex justify-content-between">
          <strong>{{ msg.applicationId }}</strong>
          <small class="text-muted">{{ msg.timestamp | date:'short' }}</small>
        </div>
        <div>{{ msg.text }}</div>
      </div>
    </div>
  </div>

  <!-- Advanced Testing Section -->
  <h3 class="mt-5 mb-3">
    <i class="material-icons" style="vertical-align: middle;">bug_report</i>
    Advanced Testing (SignalR Service Direct Methods)
  </h3>
  <p class="text-muted">Low-level SignalR testing - use only for debugging</p>

  <!-- Connection Card -->
  <div class="card mb-4">
    <div class="card-header" [class.bg-success]="isConnected" [class.bg-secondary]="!isConnected" class="text-white">
      <h5 class="mb-0">
        Connection Status:
        <span *ngIf="isConnected" class="badge badge-light text-success">Connected</span>
        <span *ngIf="!isConnected" class="badge badge-light text-secondary">Disconnected</span>
      </h5>
    </div>
    <div class="card-body">
      <div class="btn-group" role="group">
        <button
          class="btn btn-primary"
          (click)="connectToHub()"
          [disabled]="isConnected"
        >
          Connect to Hub
        </button>
        <button
          class="btn btn-warning"
          (click)="disconnectFromHub()"
          [disabled]="!isConnected"
        >
          Disconnect
        </button>
      </div>

      <div *ngIf="connectionError" class="alert alert-danger mt-3">
        <strong>Connection Error:</strong> {{ connectionError }}
      </div>

      <div *ngIf="isConnected && connectionId" class="alert alert-info mt-3 mb-0">
        <strong>Connection ID:</strong> <code>{{ connectionId }}</code>
      </div>
    </div>
  </div>

  <!-- Send Typing Notification Card -->
  <div class="card mb-4">
    <div class="card-header bg-primary text-white">
      <h5 class="mb-0">Send Typing Notification</h5>
    </div>
    <div class="card-body">
      <div class="form-group">
        <label for="applicationId">Application ID</label>
        <input
          type="text"
          class="form-control"
          id="applicationId"
          [(ngModel)]="applicationId"
          placeholder="Enter application GUID"
          [disabled]="!isConnected"
        />
      </div>

      <div class="btn-group" role="group">
        <button
          class="btn btn-primary"
          (click)="sendTypingNotification()"
          [disabled]="!isConnected || !applicationId"
          title="Send to clients in the group app_[applicationId]"
        >
          Send to Group
        </button>
        <button
          class="btn btn-outline-primary"
          (click)="sendTypingNotificationToAll()"
          [disabled]="!isConnected || !applicationId"
          title="Send to all connected clients"
        >
          Send to All Clients
        </button>
      </div>

      <p class="text-muted mt-2 mb-0">
        <small>
          <strong>Send to Group:</strong> Only clients in group "app_[applicationId]" will receive the message.<br>
          <strong>Send to All Clients:</strong> All connected clients will receive the message.
        </small>
      </p>

      <div *ngIf="typingError" class="alert alert-danger mt-3">
        <strong>Error:</strong> {{ typingError }}
      </div>
    </div>
  </div>

  <!-- Group Management Card -->
  <div class="card mb-4">
    <div class="card-header bg-success text-white">
      <h5 class="mb-0">Group Management</h5>
    </div>
    <div class="card-body">
      <div class="form-group">
        <label for="groupApplicationId">Application ID for Group</label>
        <input
          type="text"
          class="form-control"
          id="groupApplicationId"
          [(ngModel)]="groupApplicationId"
          placeholder="Enter application GUID"
          [disabled]="!isConnected"
        />
      </div>

      <div class="btn-group" role="group">
        <button
          class="btn btn-success"
          (click)="joinGroup()"
          [disabled]="!isConnected || !groupApplicationId"
        >
          Join Group
        </button>
        <button
          class="btn btn-warning"
          (click)="leaveGroup()"
          [disabled]="!isConnected || !groupApplicationId"
        >
          Leave Group
        </button>
      </div>

      <div *ngIf="groupError" class="alert alert-danger mt-3">
        <strong>Error:</strong> {{ groupError }}
      </div>

      <div *ngIf="joinedGroups.length > 0" class="mt-3">
        <h6>Joined Groups ({{ joinedGroups.length }}):</h6>
        <div class="list-group">
          <div *ngFor="let group of joinedGroups" class="list-group-item d-flex justify-content-between align-items-center">
            <span>
              <strong>app_{{ group }}</strong>
            </span>
            <button
              class="btn btn-sm btn-outline-danger"
              (click)="removeFromJoinedGroups(group)"
              title="Leave group"
            >
              Leave
            </button>
          </div>
        </div>
      </div>

      <p class="text-muted mt-3 mb-0">
        <small>Groups allow you to send messages to specific sets of clients. Join a group to receive messages sent to that group.</small>
      </p>
    </div>
  </div>

  <!-- New Message Notification Card -->
  <div class="card mb-4">
    <div class="card-header bg-warning text-dark">
      <h5 class="mb-0">
        <i class="material-icons" style="vertical-align: middle; font-size: 18px;">notifications</i>
        Send New Message Notification (SignalR Only)
      </h5>
    </div>
    <div class="card-body">
      <div class="form-group">
        <label for="messageApplicationId">Application ID</label>
        <input
          type="text"
          class="form-control"
          id="messageApplicationId"
          [(ngModel)]="applicationId"
          placeholder="Enter application GUID"
          [disabled]="!isConnected"
        />
      </div>

      <div class="form-group">
        <label for="senderName">Sender Name</label>
        <input
          type="text"
          class="form-control"
          id="senderName"
          [(ngModel)]="senderName"
          placeholder="Enter your name"
          [disabled]="!isConnected"
        />
      </div>

      <div class="form-group">
        <label for="messageText">Message Text</label>
        <textarea
          class="form-control"
          id="messageText"
          [(ngModel)]="messageText"
          placeholder="Enter message text"
          rows="3"
          [disabled]="!isConnected"
        ></textarea>
      </div>

      <button
        class="btn btn-warning"
        (click)="sendNewMessageNotification()"
        [disabled]="!isConnected || !applicationId || !messageText || !senderName"
      >
        Send Notification Only
      </button>

      <p class="text-muted mt-2 mb-0">
        <small>Notifies other clients in the group (does not save to database)</small>
      </p>

      <div *ngIf="messageError" class="alert alert-danger mt-3">
        <strong>Error:</strong> {{ messageError }}
      </div>
    </div>
  </div>

  <!-- System Service Methods Section -->
  <h3 class="mt-4 mb-3">
    <i class="material-icons" style="vertical-align: middle;">storage</i>
    System Service - Application Messaging
  </h3>
  <p class="text-muted">Production-ready messaging methods (Database + automatic SignalR notifications)</p>

  <!-- Real Message Card (Database + SignalR) -->
  <div class="card mb-4">
    <div class="card-header bg-danger text-white">
      <h5 class="mb-0">
        <i class="material-icons" style="vertical-align: middle; font-size: 18px;">send</i>
        Send Message (systemService.sendApplicationMessage)
      </h5>
    </div>
    <div class="card-body">
      <div class="form-group">
        <label for="realMessageAppId">Application ID</label>
        <input
          type="text"
          class="form-control"
          id="realMessageAppId"
          [(ngModel)]="applicationId"
          placeholder="Enter application GUID"
        />
      </div>

      <div class="form-group">
        <label for="realMessageText">Message Text</label>
        <textarea
          class="form-control"
          id="realMessageText"
          [(ngModel)]="realMessageText"
          placeholder="Enter message text"
          rows="3"
        ></textarea>
      </div>

      <button
        class="btn btn-danger"
        (click)="sendRealMessage()"
        [disabled]="!applicationId || !realMessageText"
      >
        <i class="material-icons" style="vertical-align: middle; font-size: 18px;">send</i>
        Send Message
      </button>

      <p class="text-muted mt-2 mb-0">
        <small>Saves message to database and automatically notifies all listening clients in real-time</small>
      </p>

      <div *ngIf="realMessageSuccess" class="alert alert-success mt-3">
        <strong>Success:</strong> {{ realMessageSuccess }}
      </div>

      <div *ngIf="realMessageError" class="alert alert-danger mt-3">
        <strong>Error:</strong> {{ realMessageError }}
      </div>
    </div>
  </div>

  <!-- Get Messages Card -->
  <div class="card mb-4">
    <div class="card-header bg-primary text-white">
      <h5 class="mb-0">
        <i class="material-icons" style="vertical-align: middle; font-size: 18px;">inbox</i>
        View Messages (systemService.getWorkflowApplicationMessages)
        <button
          class="btn btn-sm btn-light float-right"
          (click)="loadApplicationMessages()"
          [disabled]="!applicationId || loadingMessages"
        >
          <i class="material-icons" style="vertical-align: middle; font-size: 16px;">refresh</i>
          <span *ngIf="loadingMessages">Loading...</span>
          <span *ngIf="!loadingMessages">Refresh</span>
        </button>
      </h5>
    </div>
    <div class="card-body">
      <div class="form-group">
        <label for="messagesAppId">Application ID</label>
        <input
          type="text"
          class="form-control"
          id="messagesAppId"
          [(ngModel)]="applicationId"
          placeholder="Enter application GUID"
          [disabled]="loadingMessages"
        />
      </div>

      <div *ngIf="messagesError" class="alert alert-danger">
        <strong>Error:</strong> {{ messagesError }}
      </div>

      <div *ngIf="applicationMessages.length === 0 && !loadingMessages" class="text-muted text-center py-3">
        No messages loaded. Enter an Application ID and click "Refresh"
      </div>

      <div *ngIf="applicationMessages.length > 0" class="mt-3">
        <h6>Messages ({{ applicationMessages.length }}):</h6>
        <div class="list-group" style="max-height: 400px; overflow-y: auto;">
          <div *ngFor="let msg of applicationMessages"
               class="list-group-item"
               [class.list-group-item-light]="msg.hasRead">
            <div class="d-flex justify-content-between align-items-start">
              <div class="flex-grow-1">
                <h6 class="mb-1">
                  {{ msg.sentBy?.name || 'Unknown' }}
                  <small class="text-muted">{{ msg.sentOn | date:'short' }}</small>
                </h6>
                <p class="mb-1">{{ msg.message }}</p>
                <small class="text-muted">ID: {{ msg.id }}</small>
              </div>
              <div>
                <span *ngIf="msg.hasRead" class="badge badge-success">Read</span>
                <button
                  *ngIf="!msg.hasRead"
                  class="btn btn-sm btn-outline-primary"
                  (click)="markMessageAsRead(msg.id)"
                  title="Mark as read (systemService.markAsRead)"
                >
                  <i class="material-icons" style="vertical-align: middle; font-size: 16px;">done</i>
                  Mark Read
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>


  <!-- Event Log Section -->
  <h3 class="mt-5 mb-3">
    <i class="material-icons" style="vertical-align: middle;">list_alt</i>
    Real-time Event Log
  </h3>

  <!-- Messages Log Card -->
  <div class="card mb-4">
    <div class="card-header bg-dark text-white">
      <h5 class="mb-0">
        <i class="material-icons" style="vertical-align: middle; font-size: 18px;">notifications_active</i>
        Live Notifications & Events ({{ messages.length }})
        <button
          class="btn btn-sm btn-outline-light float-right"
          (click)="clearMessages()"
          [disabled]="messages.length === 0"
        >
          Clear
        </button>
      </h5>
    </div>
    <div class="card-body" style="max-height: 400px; overflow-y: auto;">
      <div *ngIf="messages.length === 0" class="text-muted text-center py-3">
        No events yet. Connect and start listening to see real-time events.
      </div>

      <div *ngFor="let msg of messages" class="border-bottom pb-2 mb-2">
        <div class="d-flex justify-content-between">
          <strong>{{ msg.applicationId }}</strong>
          <small class="text-muted">{{ msg.timestamp | date:'short' }}</small>
        </div>
        <div>{{ msg.text }}</div>
      </div>
    </div>
  </div>

  <!-- Advanced Testing Section -->
  <h3 class="mt-5 mb-3">
    <i class="material-icons" style="vertical-align: middle;">bug_report</i>
    Advanced Testing (Low-level SignalR Methods)
  </h3>
  <p class="text-muted">Direct SignalR hub calls for debugging - use only when needed</p>

  <!-- Send Typing Notification Card -->
  <div class="card mb-4">
    <div class="card-header bg-primary text-white">
      <h5 class="mb-0">Send Typing Notification</h5>
    </div>
    <div class="card-body">
      <div class="form-group">
        <label for="typingApplicationId">Application ID</label>
        <input
          type="text"
          class="form-control"
          id="typingApplicationId"
          [(ngModel)]="applicationId"
          placeholder="Enter application GUID"
          [disabled]="!isConnected"
        />
      </div>

      <div class="btn-group" role="group">
        <button
          class="btn btn-primary"
          (click)="sendTypingNotification()"
          [disabled]="!isConnected || !applicationId"
          title="Send to clients in the group app_[applicationId]"
        >
          Send to Group
        </button>
        <button
          class="btn btn-outline-primary"
          (click)="sendTypingNotificationToAll()"
          [disabled]="!isConnected || !applicationId"
          title="Send to all connected clients"
        >
          Send to All Clients
        </button>
      </div>

      <p class="text-muted mt-2 mb-0">
        <small>
          <strong>Send to Group:</strong> Only clients in group "app_[applicationId]" will receive the message.<br>
          <strong>Send to All Clients:</strong> All connected clients will receive the message.
        </small>
      </p>

      <div *ngIf="typingError" class="alert alert-danger mt-3">
        <strong>Error:</strong> {{ typingError }}
      </div>
    </div>
  </div>

  <!-- Group Management Card -->
  <div class="card mb-4">
    <div class="card-header bg-success text-white">
      <h5 class="mb-0">Group Management</h5>
    </div>
    <div class="card-body">
      <div class="form-group">
        <label for="groupApplicationId">Application ID for Group</label>
        <input
          type="text"
          class="form-control"
          id="groupApplicationId"
          [(ngModel)]="groupApplicationId"
          placeholder="Enter application GUID"
          [disabled]="!isConnected"
        />
      </div>

      <div class="btn-group" role="group">
        <button
          class="btn btn-success"
          (click)="joinGroup()"
          [disabled]="!isConnected || !groupApplicationId"
        >
          Join Group
        </button>
        <button
          class="btn btn-warning"
          (click)="leaveGroup()"
          [disabled]="!isConnected || !groupApplicationId"
        >
          Leave Group
        </button>
      </div>

      <div *ngIf="groupError" class="alert alert-danger mt-3">
        <strong>Error:</strong> {{ groupError }}
      </div>

      <div *ngIf="joinedGroups.length > 0" class="mt-3">
        <h6>Joined Groups ({{ joinedGroups.length }}):</h6>
        <div class="list-group">
          <div *ngFor="let group of joinedGroups" class="list-group-item d-flex justify-content-between align-items-center">
            <span>
              <strong>app_{{ group }}</strong>
            </span>
            <button
              class="btn btn-sm btn-outline-danger"
              (click)="removeFromJoinedGroups(group)"
              title="Leave group"
            >
              Leave
            </button>
          </div>
        </div>
      </div>

      <p class="text-muted mt-3 mb-0">
        <small>Groups allow you to send messages to specific sets of clients. Join a group to receive messages sent to that group.</small>
      </p>
    </div>
  </div>

  <!-- New Message Notification Card -->
  <div class="card mb-4">
    <div class="card-header bg-warning text-dark">
      <h5 class="mb-0">
        <i class="material-icons" style="vertical-align: middle; font-size: 18px;">notifications</i>
        Send New Message Notification (SignalR Only)
      </h5>
    </div>
    <div class="card-body">
      <div class="form-group">
        <label for="messageApplicationId">Application ID</label>
        <input
          type="text"
          class="form-control"
          id="messageApplicationId"
          [(ngModel)]="applicationId"
          placeholder="Enter application GUID"
          [disabled]="!isConnected"
        />
      </div>

      <div class="form-group">
        <label for="senderName">Sender Name</label>
        <input
          type="text"
          class="form-control"
          id="senderName"
          [(ngModel)]="senderName"
          placeholder="Enter your name"
          [disabled]="!isConnected"
        />
      </div>

      <div class="form-group">
        <label for="messageText">Message Text</label>
        <textarea
          class="form-control"
          id="messageText"
          [(ngModel)]="messageText"
          placeholder="Enter message text"
          rows="3"
          [disabled]="!isConnected"
        ></textarea>
      </div>

      <button
        class="btn btn-warning"
        (click)="sendNewMessageNotification()"
        [disabled]="!isConnected || !applicationId || !messageText || !senderName"
      >
        Send Notification Only
      </button>

      <p class="text-muted mt-2 mb-0">
        <small>Notifies other clients in the group (does not save to database)</small>
      </p>

      <div *ngIf="messageError" class="alert alert-danger mt-3">
        <strong>Error:</strong> {{ messageError }}
      </div>
    </div>
  </div>
</div>

